import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';

export const ASSET_NAME = 'landing-page';

// ================================================================
// Live homepage listings (Trailers Near You cards + hero ticker).
// Pulled from the Marketplace API so the homepage shows REAL
// inventory, never fabricated. Best-effort: any failure leaves the
// page fully functional (sections fall back to their static state).
// ================================================================

// ------------------------------------------------------------
// Action types
// ------------------------------------------------------------
export const FETCH_HOME_LISTINGS_REQUEST = 'app/LandingPage/FETCH_HOME_LISTINGS_REQUEST';
export const FETCH_HOME_LISTINGS_SUCCESS = 'app/LandingPage/FETCH_HOME_LISTINGS_SUCCESS';
export const FETCH_HOME_LISTINGS_ERROR = 'app/LandingPage/FETCH_HOME_LISTINGS_ERROR';

// ------------------------------------------------------------
// Reducer
// ------------------------------------------------------------
const initialState = {
  homeListingRefs: [],
  fetchHomeListingsInProgress: false,
  fetchHomeListingsError: null,
};

export default function landingPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_HOME_LISTINGS_REQUEST:
      return { ...state, fetchHomeListingsInProgress: true, fetchHomeListingsError: null };
    case FETCH_HOME_LISTINGS_SUCCESS:
      return { ...state, fetchHomeListingsInProgress: false, homeListingRefs: payload.listingRefs };
    case FETCH_HOME_LISTINGS_ERROR:
      // Non-fatal: keep any previously loaded ids, just record the error.
      return { ...state, fetchHomeListingsInProgress: false, fetchHomeListingsError: payload };
    default:
      return state;
  }
}

// ------------------------------------------------------------
// Action creators
// ------------------------------------------------------------
const fetchHomeListingsRequest = () => ({ type: FETCH_HOME_LISTINGS_REQUEST });
const fetchHomeListingsSuccess = listingRefs => ({
  type: FETCH_HOME_LISTINGS_SUCCESS,
  payload: { listingRefs },
});
const fetchHomeListingsError = e => ({
  type: FETCH_HOME_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// ------------------------------------------------------------
// Thunk: fetch newest real listings for the homepage
// ------------------------------------------------------------
const RESULT_PAGE_SIZE = 12;

export const fetchHomeListings = () => (dispatch, getState, sdk) => {
  dispatch(fetchHomeListingsRequest());

  const params = {
    pub_isInfoOnly: false,
    sort: 'createdAt', // '-createdAt' is Sharetribe default (newest first)
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.subscriptionProductName',
      'publicData.location',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.listing-card',
      'variants.listing-card-2x',
    ],
    ...createImageVariantConfig('listing-card', 400, 1),
    ...createImageVariantConfig('listing-card-2x', 800, 1),
    'limit.images': 1,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      // Store entity REFS ({ id, type }) so getMarketplaceEntities can
      // denormalise them back into full listing entities in the selector.
      const listingRefs = response.data.data.map(l => ({ id: l.id, type: 'listing' }));
      dispatch(fetchHomeListingsSuccess(listingRefs));
      return response;
    })
    .catch(e => {
      // Swallow into state; homepage must never crash on a listings failure.
      dispatch(fetchHomeListingsError(storableError(e)));
    });
};

// NOTE: hero photo reuses src/assets/subscription/hero-lot.jpg (Jordan-
// approved aerial trailer-lot), copied to src/assets/home/hero-lot.jpg.

// ------------------------------------------------------------
// loadData
// ------------------------------------------------------------
export const loadData = (params, search) => dispatch => {
  const pageAsset = { landingPage: `content/pages/${ASSET_NAME}.json` };
  // Kick off both; do not let the listings fetch block/fault the page asset.
  dispatch(fetchHomeListings());
  return dispatch(fetchPageAssets(pageAsset, true));
};
